import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { supabase } from "@/integrations/supabase/client";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Compact date for table cells: "Jul 30" for this year, "Jan 14, 2026" for any
 * other. A wide table often carries two or three date columns, and "MMM d,
 * yyyy" pins each to ~90px of nowrap width — width the readable columns need
 * more. Dropping the year only where it's the current one keeps every date
 * unambiguous.
 */
export function shortDate(value: string | number | Date | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = "numeric";
  return d.toLocaleDateString("en-US", opts);
}

/**
 * Upload a file directly to Supabase storage
 * @param file - The file to upload
 * @param bucket - The storage bucket name
 * @param fileName - The file name/path in the bucket
 * @returns The public URL of the uploaded file
 */
export async function uploadFile(
  file: File,
  bucket: string,
  fileName: string
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(fileName, file, {
      contentType: file.type,
      upsert: true,
    });

  if (error) {
    throw new Error(`Upload failed: ${error.message}`);
  }

  const { data: publicUrlData } = supabase.storage
    .from(bucket)
    .getPublicUrl(fileName);

  return publicUrlData.publicUrl;
}
